/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Title, Tooltip, UnstyledButton} from '@mantine/core';
import {useMediaQuery} from '@mantine/hooks';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import navigateToURL from 'lib/navigateToURL';
import {registerChatListingRefresh} from 'llm/chat-library/ChatListingRefresh';
import AgentFactoriesListPage from 'llm/chat-library/factories/AgentFactoriesListPage';
import {
  PromptSuggestionCardGallery,
  SignedOutPromptSuggestionCardGallery,
} from 'llm/chat-library/prompt-suggestions/PromptSuggestionCards';
import {MCPEnabledServersProvider} from 'llm/mcp-client-library/MCPEnabledServersContext';
import useRealtimeVoiceSession from 'llm/realtime-voice/useRealtimeVoiceSession';
import useVoiceUIController from 'llm/realtime-voice/VoiceUIController';
import {chatSettingsModalUrl, getChatUrlPath} from 'llm/routes/UrlUtils';
import {LOGGED_OUT_MODAL_KEY} from 'nav/LoggedOutModal';
import SharedFileSidepanel from 'nav/share/growth/SharedFileSidepanel';
import {ShareButton} from 'nav/share/ShareBar';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import ToggleDropdown from 'nav/ToggleDropdown';
import {RealtimeMessageType} from 'realtime/Constants';
import {useRealtimeChannelRefetch} from 'realtime/RealtimeUpdateSubscriptionWrapper';
import {getRealtimeChannelNameForUserId} from 'realtime/util/RealtimeUtil';
import {getActiveShareLinkItemId} from 'ShareAccess';
import EntityStore from 'stores/EntityStore';
import {ZINDEX_SIDE_NAVBAR} from 'styles/StyleConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import useOnMount from 'ui-library/hooks/useOnMount';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {isServerRequestError} from 'util/Net';
import {assertNever} from 'util/Types';

import {AcademicFreeChatBannerIfVisible} from '../AcademicFreeChatBanner';
import AITrialSignupModal from '../auth/AITrialSignupModal';
import {AIWarning, NewChatInput} from '../ChatInput';
import type {NewChatInputHandle} from '../ChatInput';
import ProgressiveDisclosure, {
  PROGRESSIVE_DISCLOSURE_ENTER_TRANSITION_MS,
  PROGRESSIVE_DISCLOSURE_EXIT_TRANSITION_MS,
} from '../progressive-disclosure/ProgressiveDisclosure';
import * as RealtimeVoiceChatHandoff from '../RealtimeVoiceChatHandoff';
import {ChatSettingsModalContainer} from '../shared/list-detail/ChatSettingsModal';
import {
  REDESIGN_DRAWER_BACKGROUND,
  REDESIGN_DRAWER_BORDER,
  REDESIGN_DRAWER_HORIZONTAL_PADDING_PX,
  REDESIGN_DRAWER_INNER_SHADOW,
  REDESIGN_DRAWER_VERTICAL_PADDING_PX,
  REDESIGN_DRAWER_WIDTH_CSS_VAR,
  REDESIGN_DRAWER_WIDTH_PX,
  REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  REDESIGN_HEADER_ICON_CHIP_INSET_PX,
  REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
  REDESIGN_HEADER_ICON_COLOR,
} from '../ui/AIStyleConstants';
import {AIInterestFirstChatScreen} from './AIInterestFirstChatScreen';
import {AIOnboardingScreen} from './AIOnboardingScreen';
import {HAS_SEEN_AI_SIDEPANEL_ONBOARDING, AISettingsContent} from './AISettings';
import {
  AISettingsTab,
  ChatSettingsTab,
  InitialModal,
  useAISidepanelGlobalClientState,
} from './AISidepanelV2GlobalClientState';
import ChatListing from './ChatListing';
import ChatRouter from './ChatRouter';
import type {CreateChatResult, SidepanelChat} from './ChatRouter';
import type {ChatSessionStarUpdate} from './ChatSessionMenu';
import {doesAgentTypePreferExpandedSidepanel, isAISidepanelEnabled} from './ChatTypeUtil';
import {NoAccessChatListing} from './NoAccessChatListing';
import {OnboardingBanner} from './OnboardingBanner';
import {
  ONBOARDING_INVITE_TRANSITION_MS,
  OnboardingInviteExitFade,
  OnboardingScreenEnterFade,
} from './OnboardingComponents';
import {restartOnboardingSession, startOnboardingSession} from './OnboardingDAO';
import {OnboardingQuestionLoadingView, OnboardingScreenShell} from './OnboardingQuestionView';
import ReadOnlyChat from './ReadOnlyChat';
import ChatSidebarNav from './redesign/ChatSidebarNav';
import DrawerResizeHandle from './redesign/DrawerResizeHandle';
import GroupedChatListing, {type ChatHistoryActions} from './redesign/GroupedChatListing';
import RedesignChatSidebar from './redesign/RedesignChatSidebar';
import RedesignHeaderTitle from './redesign/RedesignHeaderTitle';
import RedesignMinimizedHeader, {MINIMIZED_HEADER_HEIGHT} from './redesign/RedesignMinimizedHeader';
import RedesignPanelToggle from './redesign/RedesignPanelToggle';
import RedesignSidebarToggle from './redesign/RedesignSidebarToggle';
import useResizableDrawer from './redesign/useResizableDrawer';

/** Matches `less/variables.less` `@width--sidepanel-plus`. */
export const SIDEPANEL_SEMI_EXPANDED_WIDTH = 480;
const HISTORY_DRAWER_WIDTH = 286;

/**
 * Delay before the sidebar opens after the sidepanel expands, so the sidebar reveal trails the
 * panel's ~300ms width animation instead of popping in simultaneously. See `useHistoryAutoHide`.
 *
 * @visibleForTesting
 */
export const SIDEBAR_EXPAND_DELAY_MS = 450;

export interface AISidepanelProps {
  isExpanded: boolean;
}

function openTrialSignUpModal(trigger: string): void {
  LegacyModalContainer.open(
    LOGGED_OUT_MODAL_KEY,
    <AITrialSignupModal
      trigger={trigger}
      onRequestClose={() => {
        LegacyModalContainer.close(LOGGED_OUT_MODAL_KEY);
      }}
    />
  );
}

function WelcomeToChatPanel(props: {
  isNarrow: boolean;
  onCreateChat: (result: CreateChatResult) => void;
  isSignedOut: boolean;
  voiceHandoffScopeId: string;
  /** When true, the onboarding invite fades out before the survey mounts. */
  isOnboardingInviteExiting?: boolean;
  /**
   * When provided (and signed in), the welcome panel shows the inline jellyfish onboarding banner in
   * place of the hero so onboarding stays optional — the user can ignore it and type a chat directly.
   */
  onboardingBannerProps?: {
    onStart: () => void;
    onSkip: () => void;
  };
}): JSX.Element {
  const showOnboardingBanner = Boolean(props.onboardingBannerProps) && !props.isSignedOut;
  const showProgressiveDisclosure = !props.isSignedOut;
  // The onboarding banner replaces only the hero. The progressive-disclosure chips stay (they're a
  // primary "start a chat directly" path, so onboarding remains optional).
  const showSuggestedActions = props.isSignedOut;

  const [isPreviewText, setIsPreviewText] = useState(false);
  const [hasUserInput, setHasUserInput] = useState(false);
  const realtimeVoiceSessionControls = useRealtimeVoiceSession(null);
  const voiceUIController = useVoiceUIController(realtimeVoiceSessionControls);
  const newChatInputRef = useRef<NewChatInputHandle>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const handleHoverOption = useCallback((prompt: string | null) => {
    if (prompt) {
      newChatInputRef.current?.setPreviewText(prompt);
      setIsPreviewText(true);
    } else {
      newChatInputRef.current?.clearPreviewText();
      setIsPreviewText(false);
    }
  }, []);

  const handleSelectOption = useCallback((prompt: string) => {
    setIsPreviewText(false);
    newChatInputRef.current?.submitWithText(prompt);
  }, []);

  const newChatInput = (
    <NewChatInput
      ref={newChatInputRef}
      onCreateChat={props.onCreateChat}
      isPreviewText={isPreviewText}
      onHasUserInputChange={setHasUserInput}
      voiceUIController={voiceUIController}
      voiceHandoffScopeId={props.voiceHandoffScopeId}
    />
  );

  const renderWelcomeBanner = (layoutIsExpanded: boolean, isLayoutContentVisible: boolean): React.ReactNode =>
    showOnboardingBanner && props.onboardingBannerProps ? (
      <OnboardingBanner {...props.onboardingBannerProps} />
    ) : (
      <div
        aria-hidden={!isLayoutContentVisible}
        style={{
          opacity: isLayoutContentVisible ? 1 : 0,
          transition: prefersReducedMotion
            ? undefined
            : `opacity ${
                isLayoutContentVisible
                  ? PROGRESSIVE_DISCLOSURE_ENTER_TRANSITION_MS
                  : PROGRESSIVE_DISCLOSURE_EXIT_TRANSITION_MS
              }ms ease`,
        }}
        className={css(styles.welcomeHero, !layoutIsExpanded && styles.welcomeHeroCompact)}
      >
        <Title
          order={2}
          size={layoutIsExpanded ? 'h2' : 'h3'}
          style={{
            textAlign: layoutIsExpanded ? 'center' : 'left',
          }}
          className={css(styles.welcomeHeader)}
        >
          What can I help with today?
        </Title>
        {!props.isSignedOut && BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT && (
          <div className={css(styles.publicTrialBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text="You are on a free trial of Benchling AI."
              supportingInfo={
                <div>
                  <p>
                    All data is stored{' '}
                    <a href="https://www.benchling.com/trust" target="_blank" rel="noopener noreferrer">
                      securely
                    </a>
                    , but do not upload sensitive data unless you have permission from your organization.
                  </p>
                  <p>
                    If you are an existing Benchling customer, you can use Benchling AI in your organization's
                    Benchling instance. Contact your administrator for access to Benchling AI.
                  </p>
                </div>
              }
            />
          </div>
        )}
      </div>
    );

  return (
    <OnboardingWelcomeLayout
      isExpanded={!props.isNarrow}
      isOnboarding={showOnboardingBanner}
      newChatInput={newChatInput}
      renderWelcomeBanner={renderWelcomeBanner}
      showProgressiveDisclosure={showProgressiveDisclosure}
      suggestedActions={
        showSuggestedActions ? (
          <div className={css(styles.welcomeFooter)}>
            {props.isSignedOut && BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT ? (
              <SignedOutPromptSuggestionCardGallery />
            ) : (
              <PromptSuggestionCardGallery />
            )}
          </div>
        ) : null
      }
      hasUserInput={hasUserInput}
      isOnboardingInviteExiting={props.isOnboardingInviteExiting === true}
      onHoverOption={handleHoverOption}
      onSelectOption={handleSelectOption}
    />
  );
}

function isActiveShareLink(chatId: string | null): boolean {
  return chatId != null && getActiveShareLinkItemId() === chatId;
}

/**
 * If the sidepanel is expanded, set the URL appropriately.
 */
function useSyncStateToURL(isExpanded: boolean): void {
  const [panelState] = useAISidepanelGlobalClientState();
  const isShareLink = isActiveShareLink(panelState.activeChat.id);
  // When the chat settings modal is open, its route (/chats/settings/...) owns the URL, so this
  // chat-URL sync must not clobber it. Likewise, the Agent Factories list view owns its own route
  // (/chats/agent-factories). A single factory instead opens as its own workspace tab (see
  // EditorRoutes.agentFactoryEditor), which is not tracked in `showAgentFactoriesView`.
  const isChatSettingsModalOpen = panelState.chatSettingsModal != null;
  const isAgentFactoriesViewOpen = panelState.showAgentFactoriesView;
  useEffect(() => {
    // We only control the URL if we are expanded; otherwise, whatever is open in the workspace sets the URL.
    if (isExpanded && !isShareLink && !isChatSettingsModalOpen && !isAgentFactoriesViewOpen) {
      // TODO: rewrite the url to match the share link url

      const newPath = getChatUrlPath(panelState.activeChat.id ?? 'new');

      // Leave the URL alone if the path matches. This ensures that any querystring parameters are preserved,
      // which is important for marketing (GoogleTagManagerForAI.ts)
      if (newPath === window.location.pathname) {
        return;
      }
      navigateToURL(newPath, {
        replace: false, // No need to replace: navigateToURL already no-ops if the URL hasn't changed.
        skipTrigger: true, // we are syncing the state to the URL, so no need to trigger the route function
      });
    }
  }, [panelState, isExpanded, isShareLink, isChatSettingsModalOpen, isAgentFactoriesViewOpen]);
}

/**
 * On collapse (to the semi-expanded view) auto-hide the sidebar; on expand open it. In the redesign
 * the sidebar *always* opens on expand and its reveal trails the sidepanel's width animation by
 * {@link SIDEBAR_EXPAND_DELAY_MS} (so it slides in after the panel has widened rather than popping in
 * at the same instant); legacy instead restores the user's last explicit preference immediately.
 * Combined with the closed-by-default state, this yields the redesign's "closed in the collapsed
 * view, open in the expanded view" behavior. Keyed off `isWideView` (not raw expansion) so a phone —
 * always the collapsed chrome — never opens the sidebar.
 *
 * The latest panel state is read from a ref at write-time (rather than closed over) so the effect can
 * depend only on the `isWideView` transition; that keeps an unrelated panel-state change from
 * re-running the effect and cancelling a pending open timer.
 */
function useHistoryAutoHide(isWideView: boolean, isRedesign: boolean): void {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();

  // Keep the newest panel state accessible to a delayed timer without making it an effect dependency.
  const panelStateRef = useRef(panelState);
  panelStateRef.current = panelState;

  // Seed to false in the redesign so a panel whose very first render is already wide (e.g. a future
  // persisted-expansion preference, an AI-trial landing that boots expanded, or Storybook/RTL rendering
  // it with isExpanded) still runs the narrow→wide branch once and opens the sidebar. Legacy stays
  // transition-only (seeded to the current view) so its first render never forces showHistory.
  const prevIsWideView = useRef(isRedesign ? false : isWideView);
  useEffect(() => {
    const wasWideView = prevIsWideView.current;
    prevIsWideView.current = isWideView;

    if (wasWideView && !isWideView) {
      setPanelState({...panelStateRef.current, showHistory: false});
      return undefined;
    }
    if (!wasWideView && isWideView) {
      // Redesign always opens the sidebar on expand; legacy restores the user's last preference.
      const openSidebar = (): void => {
        const latest = panelStateRef.current;
        setPanelState({...latest, showHistory: isRedesign ? true : latest.showHistoryPreference});
      };
      // Only the redesign's always-open reveal is delayed to trail the panel's width animation.
      if (!isRedesign) {
        openSidebar();
        return undefined;
      }
      const timerId = window.setTimeout(openSidebar, SIDEBAR_EXPAND_DELAY_MS);
      // Cancel the pending open if we collapse again before it fires.
      return () => {
        window.clearTimeout(timerId);
      };
    }
    return undefined;
  }, [isWideView, isRedesign, setPanelState]);
}

/**
 * On mobile-size screens, we trigger expand/close events if we are ever in a
 * semi-expanded state.
 */
function useDisallowSemiExpandedOnMobile(isExpanded: boolean, isPhoneWidth: boolean): void {
  const lastIsExpanded = useRef<boolean | null>(null);
  useEffect(() => {
    if (isPhoneWidth && !isExpanded) {
      if (lastIsExpanded.current === true) {
        // If we moved from expanded to semi-expanded, close the sidepanel, unmounting us.
        SidepanelActions.close();
      } else {
        // If we just mounted (i.e. just opened the sidepanel) or switched to phone width
        // while semi-expanded, expand to full view.
        SidepanelActions.expand();
      }
    }
    lastIsExpanded.current = isExpanded;
  }, [isExpanded, isPhoneWidth]);
}

function SignedInHeader({
  isExpanded,
  isWideView,
  isPhoneWidth,
  isShareLink,
  hasChatAccess,
  isOnboarding,
  isRedesignLayout,
  onStarUpdated,
}: {
  isExpanded: boolean;
  isWideView: boolean;
  isPhoneWidth: boolean;
  isShareLink: boolean;
  hasChatAccess: boolean;
  isOnboarding: boolean;
  isRedesignLayout: boolean;
  onStarUpdated?: (update: ChatSessionStarUpdate) => Promise<void>;
}): JSX.Element {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();
  const {llmEnableChatRedesign} = useRolloutFlags();
  const isSettingsOpen = panelState.activeSettingsTab != null;

  const handleSettingsClick = useCallback(() => {
    if (!isSettingsOpen) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_SETTINGS_PANEL);
      setPanelState({...panelState, activeSettingsTab: AISettingsTab.GENERAL});
      return;
    }
    setPanelState({...panelState, activeSettingsTab: null});
  }, [isSettingsOpen, panelState, setPanelState]);

  const handleCloseAIView = useCallback(() => {
    SidepanelActions.close();
  }, []);

  const onOpenHistory = useCallback(() => {
    // If the user toggles the history, and capture it as their last explicit toggle.
    setPanelState({
      ...panelState,
      activeSettingsTab: null,
      showHistory: !panelState.showHistory,
      showHistoryPreference: !panelState.showHistory,
    });
  }, [setPanelState, panelState]);

  const onResumeNewChat = useCallback((): void => {
    // If the user is already viewing the welcome panel, focus the chat input.
    if (panelState.activeChat.id == null) {
      // TODO(BNCH-132631): Use a ref to the ChatInput component.
      (document.querySelector('.sidepanel textarea') as HTMLTextAreaElement | undefined)?.focus();
    }
    setPanelState({
      ...panelState,
      activeChat:
        // Reset the active chat if we are returning to the welcome panel.
        panelState.activeChat.id != null ? {id: null} : panelState.activeChat,
      activeSettingsTab: null,
      showAgentFactoriesView: false,
      // Redesign: the sidebar toggle is only changed by the user. Legacy: only show history if we
      // have the screen real estate.
      ...(llmEnableChatRedesign ? {} : {showHistory: panelState.showHistoryPreference && isWideView}),
    });
  }, [setPanelState, panelState, isWideView, llmEnableChatRedesign]);

  return (
    <div className={classNames(css(styles.sidepanelHeader, isRedesignLayout && styles.redesignHeader))}>
      <div
        className={classNames(
          css(
            // HACK: Ideally these buttons are part of the drawer; otherwise, the header is not centered.
            // These styles cause the buttons to take up the same space as the drawer, thus pushing
            // the header back to the center. In the redesign the drawer is a separate top-level
            // element and the header is shifted via margin, so this width hack is not used.
            styles.leftHeaderButtons,
            !isRedesignLayout && panelState.showHistory && isWideView && styles.leftHeaderButtonsExpanded
          )
        )}
      >
        {/* Redesign: the sidebar toggle is a standalone floating hamburger, so the header's left
            cluster is empty. Legacy keeps the history + new-chat buttons. */}
        {!isOnboarding && !isRedesignLayout && (
          <>
            {/* NB: Tooltip child needs to accept a ref, but our Button doesn't, so wrap in a span. */}
            <Tooltip label="Chat history">
              <span>
                <Button
                  aria-label="Chat history"
                  buttonStyle={ButtonStyle.TERTIARY_WHITE}
                  className={classNames({'is-active': panelState.showHistory})}
                  icon="history"
                  onClick={onOpenHistory}
                  size={ButtonSize.SMALL}
                />
              </span>
            </Tooltip>
            <Tooltip label="New chat" disabled={isWideView}>
              {/* Use u-flex-none to avoid text from wrapping when expanding / collapsing the sidepanel. */}
              <span className="u-flex-none">
                <Button
                  aria-label="New chat"
                  buttonStyle={ButtonStyle.TERTIARY_WHITE}
                  icon="edit"
                  onClick={onResumeNewChat}
                  size={ButtonSize.SMALL}
                  text={isWideView ? 'New chat' : null}
                />
              </span>
            </Tooltip>
          </>
        )}
      </div>
      {isRedesignLayout && panelState.activeChat.id != null && !panelState.showAgentFactoriesView ? (
        // RedesignHeaderTitle owns the heading role and renders nothing until the title resolves, so
        // this wrapper stays a plain layout slot (no empty heading while loading).
        <div className={css(styles.headerTitle)}>
          <RedesignHeaderTitle chatId={panelState.activeChat.id} onStarUpdated={onStarUpdated} />
        </div>
      ) : (
        <div role="heading" aria-level={1} className={css(styles.headerTitle)}>
          <UnstyledButton fz="18px" onClick={onResumeNewChat} tabIndex={0}>
            Benchling AI
          </UnstyledButton>
        </div>
      )}
      <div className={css(styles.rightHeaderButtons)}>
        {!isOnboarding &&
          // ShareButton also checks this flag, but this prevents rendering an empty tooltip wrapper.
          BenchlingConfig.ENABLE_SHARE_LINK &&
          panelState.activeChat.id &&
          !panelState.showAgentFactoriesView &&
          // We don't support "resharing" from a shared chat, so don't show the share button.
          !isShareLink && (
            <Tooltip label="Share">
              <span
                className={isRedesignLayout ? css(styles.redesignShareChip) : undefined}
                {...getTestProps({component: 'AISidepanel', element: 'share-button-wrapper'})}
              >
                <ShareButton
                  fileType={ModelTypeConstants.CHAT_SESSION}
                  fileId={panelState.activeChat.id}
                  fileName="my chat" // TODO: Use a GraphQL query.
                  buttonSize={ButtonSize.SMALL}
                  // The redesign header's controls are borderless glyphs, so drop the bordered chip.
                  buttonStyle={isRedesignLayout ? ButtonStyle.TRANSPARENT : ButtonStyle.TERTIARY_WHITE}
                  filePerms={{writable: true}} // TODO: graphql query
                  iconOnly={true}
                />
              </span>
            </Tooltip>
          )}
        {/* Redesign moves settings into the sidebar toolbar, so the header omits the gear(s). */}
        {!isOnboarding && hasChatAccess && !isRedesignLayout && (
          <Tooltip label="Settings" disabled={isSettingsOpen}>
            <span>
              <Button
                aria-label="Settings"
                buttonStyle={ButtonStyle.TERTIARY_WHITE}
                className={classNames({'is-active': isSettingsOpen})}
                icon="gear"
                onClick={handleSettingsClick}
                size={ButtonSize.SMALL}
              />
            </span>
          </Tooltip>
        )}
        {isPhoneWidth ||
        (panelState.activeChat.id != null && panelState.activeChat.type === ChatType.LEGACY_DATA_IMPORT) ? (
          <Button
            icon="close"
            onClick={handleCloseAIView}
            buttonStyle={ButtonStyle.TRANSPARENT}
            size={ButtonSize.SMALL}
            dataTestAttrs={{component: 'AISidepanel', element: 'close-button'}}
          />
        ) : isRedesignLayout ? (
          <RedesignPanelToggle expanded={isExpanded} />
        ) : (
          <ToggleDropdown expanded={isExpanded} />
        )}
      </div>
    </div>
  );
}

/**
 * Header for both LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT and for chats accessed via share link.
 */
function SignedOutHeader({isShareLink}: {isShareLink: boolean}): JSX.Element {
  return (
    <div
      className={classNames(
        css(styles.sidepanelHeaderSignedOut, isShareLink && styles.sidepanelHeaderSignedOutShareLink)
      )}
    >
      <div role="heading" aria-level={1} className={css(styles.headerTitleSignedOut)}>
        <BenchlingIcon icon="benchling-logo" className={css(styles.headerTitleLogoSignedOut)} />
        <span className={css(styles.headerTitleTextSignedOut)}>Benchling AI</span>
      </div>
      <div>
        {!isShareLink && (
          <div className={css(styles.headerButtonsSignedOut)}>
            <ButtonLink
              className="u-flex-none"
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.SECONDARY_WHITE}
              href="/signin?next=/chats/new"
            >
              Sign in
            </ButtonLink>
            <ButtonLink
              className="u-flex-none"
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.PRIMARY}
              href="/signup?next=/chats/new"
              onClick={(e) => {
                if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT) {
                  e.preventDefault();
                  openTrialSignUpModal('signup_button_click');
                }
              }}
            >
              Sign up
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  );
}

export function AISidepanel(props: AISidepanelProps): JSX.Element {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();
  // The sidepanel owns the handoff across the normal welcome-to-created-chat replacement. Only
  // unmounting the surrounding sidepanel cancels creation or a claimed automatic Voice start.
  const [voiceHandoffScopeId] = useState(RealtimeVoiceChatHandoff.createRealtimeVoiceChatHandoffScope);
  const {llmEnableChatRedesign} = useRolloutFlags();
  const refetchRef = useRef<() => void>(() => {});
  const currentUserId = EntityStore.currentUserId;
  const userChannelName = currentUserId == null ? '' : getRealtimeChannelNameForUserId(currentUserId);
  const historyActionsRef = useRef<ChatHistoryActions | null>(null);
  const handleStarUpdated = useCallback(
    (update: ChatSessionStarUpdate) =>
      historyActionsRef.current?.handleStarUpdated(update) ?? Promise.resolve(),
    []
  );
  const isPhoneWidth = useIsPhoneWidth();
  const isWideView = !isPhoneWidth && props.isExpanded;

  useEffect(
    () => () => {
      RealtimeVoiceChatHandoff.cancelRealtimeVoiceChatHandoff(voiceHandoffScopeId);
    },
    [voiceHandoffScopeId]
  );

  useSyncStateToURL(props.isExpanded);
  useHistoryAutoHide(isWideView, llmEnableChatRedesign);
  useDisallowSemiExpandedOnMobile(props.isExpanded, isPhoneWidth);
  useRealtimeChannelRefetch({
    channelName: userChannelName,
    refetch: () => {
      refetchRef.current();
    },
    messageFilter: [RealtimeMessageType.CHAT_SESSION_CREATED],
    disabled: currentUserId == null,
  });

  useEffect(
    () =>
      registerChatListingRefresh(() => {
        refetchRef.current();
      }),
    []
  );

  useOnMount(() => {
    const isSignedOut = EntityStore.currentUser() == null;
    trackEvent(
      isSignedOut && BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT
        ? LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_AI_TRIAL_HOMEPAGE
        : LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_SIDEPANEL
    );
    if (panelState.initialModal != null) {
      switch (panelState.initialModal) {
        case InitialModal.SIGNUP_MODAL:
          if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT) {
            openTrialSignUpModal('signup_url');
          }
          break;
        default:
          assertNever(panelState.initialModal);
      }
    }
  });

  // NOTE: It's a bit sketchy that our callback is dependent on the current panelState. There is some risk
  // that the component will invoke the callback asynchronously where it will invoke the stale callback.
  // React's setState avoids this by having a callback API: `setState((oldState) => )`, which
  // useGlobalClientState does not support. This will be a bigger issue if there is intentionally other
  // state that the user accumulates while waiting for the chat to be created. In practice, it may not
  // be an issue since creating a chat on the backend is a relatively quick operation.
  const onCreateChat = useCallback(
    (result: CreateChatResult): void => {
      setPanelState({
        ...panelState,
        activeChat: {id: result.chatSessionId, type: result.chatType},
        showAgentFactoriesView: false,
      });
      refetchRef.current();
      if (doesAgentTypePreferExpandedSidepanel(result.initialAgentType)) {
        SidepanelActions.expand();
      }
    },
    [setPanelState, panelState, refetchRef]
  );

  const onOpenChat = useCallback(
    (chat: SidepanelChat) => {
      // If the user clicks on a chat in semi-expanded or mobile, consider that an implicit toggle close of the history.
      const showHistoryPreference = panelState.showHistoryPreference && isWideView;
      setPanelState({
        ...panelState,
        // If a different chat was selected, pick a new client id to reset any component state.
        clientChatId: panelState.activeChat.id === chat.id ? panelState.clientChatId : uuidv4(),
        activeChat: chat,
        showAgentFactoriesView: false,
        // Redesign: in the wide view the sidebar is persistent (leave it untouched); in the
        // semi-expanded view, selecting a chat closes the dropdown. Legacy: clicking a chat implicitly
        // closes the history drawer unless there is room to keep it open.
        ...(llmEnableChatRedesign
          ? isWideView
            ? {}
            : {showHistory: false}
          : {showHistoryPreference, showHistory: showHistoryPreference && isWideView}),
      });
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT_FROM_HISTORY, {
        chatId: chat.id,
        chatType: chat.id != null ? chat.type : null,
      });
    },
    [setPanelState, panelState, isWideView, llmEnableChatRedesign]
  );

  // Redesign: "New Chat" nav item resets to the welcome panel. In the semi-expanded view it also
  // closes the dropdown; in the wide view the sidebar is persistent so the toggle is left alone.
  const onNewChat = useCallback(() => {
    setPanelState({
      ...panelState,
      activeChat: panelState.activeChat.id != null ? {id: null} : panelState.activeChat,
      activeSettingsTab: null,
      showAgentFactoriesView: false,
      ...(llmEnableChatRedesign && !isWideView ? {showHistory: false} : {}),
    });
  }, [setPanelState, panelState, llmEnableChatRedesign, isWideView]);

  // Redesign: hamburger toggle (available both in the sidebar toolbar and the header when closed).
  const onToggleSidebar = useCallback(() => {
    setPanelState({
      ...panelState,
      activeSettingsTab: null,
      showHistory: !panelState.showHistory,
      showHistoryPreference: !panelState.showHistory,
    });
  }, [setPanelState, panelState]);

  const onToggleSidebarFromHamburger = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_AI_SIDEPANEL_HAMBURGER, {
      isPanelExpanded: isWideView,
      isMenuOpen: panelState.showHistory,
    });
    onToggleSidebar();
  }, [isWideView, onToggleSidebar, panelState.showHistory]);

  // Hide the sidebar immediately so it collapses out of the way; useHistoryAutoHide reopens it once the
  // panel finishes expanding into the wide view.
  const onExpandPanelFromSidebar = useCallback(() => {
    setPanelState({...panelState, activeSettingsTab: null, showHistory: false});
    SidepanelActions.expand();
  }, [setPanelState, panelState]);

  const isShareLink = isActiveShareLink(panelState.activeChat.id);
  const isSignedOut = EntityStore.currentUser() == null;
  const hasChatAccess = isAISidepanelEnabled();
  // The redesigned sidebar layout (top-level drawer, shifted header/body) applies only for signed-in
  // users with chat access when the flag is on; everything else keeps the legacy layout.
  const useRedesignLayout = !isSignedOut && llmEnableChatRedesign && hasChatAccess;

  // The redesign uses one persistent drawer for both views: it unclips to the right (growing width) in
  // the wide (fully-expanded) view and downward (growing max-height) in the semi-expanded view
  // (`!isWideView`, i.e. not expanded or on a phone). Those reveals happen on different axes, so
  // animating the drawer across a view change would look diagonal. We follow the standard "only
  // animate while open" technique — the transition is applied via JS only when the drawer is open, and
  // additionally suppressed on the single render where the view flips — so switching views re-clips the
  // drawer instantly while opening/closing within a view still animates.
  const prevIsWideViewRef = useRef(isWideView);
  const wideViewJustChanged = prevIsWideViewRef.current !== isWideView;
  useEffect(() => {
    prevIsWideViewRef.current = isWideView;
  }, [isWideView]);
  // `showHistory` is auto-hidden a tick later (in an effect) when collapsing to the semi-expanded
  // view, so ignore its momentarily-stale open value on that render to avoid flashing the dropdown.
  const isDrawerOpen = useRedesignLayout && panelState.showHistory && !(wideViewJustChanged && !isWideView);
  // Animate open/close within a view, but never on the view-flip render (that is when the hidden
  // position changes axis, and animating it would look diagonal).
  const isDrawerAnimated = useRedesignLayout && !wideViewJustChanged;

  // Manual drag-to-resize is only meaningful in the wide view, where the drawer is a left column
  // beside the chat (in the semi-expanded view it's a top dropdown overlay).
  const isDrawerResizable = isWideView && isDrawerOpen;
  const {
    width: drawerWidth,
    isResizing: isDrawerResizing,
    handleMouseDown: onDrawerResizeMouseDown,
    containerRef: redesignRootRef,
  } = useResizableDrawer({enabled: isDrawerResizable});

  const [hasSeenOnboarding, setHasSeenOnboarding] = usePreference<boolean | undefined>(
    HAS_SEEN_AI_SIDEPANEL_ONBOARDING
  );
  const showOnboarding = hasChatAccess && !hasSeenOnboarding && !isSignedOut;

  // `showOnboardingScreen` gates the full-screen onboarding flow. While false, a new user lands on the
  // welcome panel (which surfaces the inline jellyfish banner) so onboarding stays optional — they can
  // ignore it and type a chat directly. The banner's "Start personalizing" flips it to true.
  const showOnboardingScreen = panelState.showOnboardingScreen;
  const setShowOnboardingScreen = useCallback(
    (value: boolean) => {
      setPanelState({...panelState, showOnboardingScreen: value});
    },
    [panelState, setPanelState]
  );

  // Within the full-screen flow, which step is showing: the LLM agent survey ('AGENT') or the static
  // interest question that routes the user into their first chat ('INTEREST').
  const [onboardingPhase, setOnboardingPhase] = useState<'AGENT' | 'INTEREST'>('AGENT');
  // Questions answered during the agent survey, carried into the interest screen so its progress dots
  // continue from the running total rather than resetting.
  const [agentAnsweredCount, setAgentAnsweredCount] = useState(0);

  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [isOnboardingInviteExiting, setIsOnboardingInviteExiting] = useState(false);
  const onboardingInviteExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (onboardingInviteExitTimerRef.current != null) {
        clearTimeout(onboardingInviteExitTimerRef.current);
      }
    },
    []
  );

  // "Start personalizing" from the welcome-panel banner: fade out the invite, then enter the survey.
  const handleBannerStart = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_ONBOARDING_BANNER_START);
    setOnboardingPhase('AGENT');
    if (prefersReducedMotion) {
      setShowOnboardingScreen(true);
      return;
    }
    setIsOnboardingInviteExiting(true);
    onboardingInviteExitTimerRef.current = setTimeout(() => {
      onboardingInviteExitTimerRef.current = null;
      setShowOnboardingScreen(true);
      setIsOnboardingInviteExiting(false);
    }, ONBOARDING_INVITE_TRANSITION_MS);
  }, [prefersReducedMotion, setShowOnboardingScreen]);

  // "Maybe later" from the banner: mark onboarding seen so the welcome panel becomes the plain landing.
  const handleBannerSkip = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_ONBOARDING_BANNER_DISMISS);
    setHasSeenOnboarding(true);
  }, [setHasSeenOnboarding]);

  // Skipping/exiting once inside the survey: mark onboarding seen and drop the user on the settings
  // surface. With the redesign flag on, that surface is the new settings modal (URL-driven, opened by
  // navigating to its route — the same way the redesign sidebar opens it); we clear activeSettingsTab
  // so the legacy drawer does not also render. With the flag off, we open the legacy settings drawer.
  const dismissOnboardingToSettings = useCallback(() => {
    setHasSeenOnboarding(true);
    if (llmEnableChatRedesign) {
      setPanelState({...panelState, activeSettingsTab: null, showOnboardingScreen: false});
      navigateToURL(chatSettingsModalUrl(ChatSettingsTab.GENERAL));
      return;
    }
    setPanelState({
      ...panelState,
      activeSettingsTab: AISettingsTab.GENERAL,
      showOnboardingScreen: false,
    });
  }, [setHasSeenOnboarding, panelState, setPanelState, llmEnableChatRedesign]);

  // Closing the interest screen via the top-right ✕: mark onboarding seen and drop the user on the
  // new chat page (the WelcomeToChatPanel) rather than the settings page.
  const dismissOnboardingToNewChat = useCallback(() => {
    setHasSeenOnboarding(true);
    setPanelState({
      ...panelState,
      activeChat: {id: null},
      activeSettingsTab: null,
      showOnboardingScreen: false,
    });
  }, [setHasSeenOnboarding, panelState, setPanelState]);

  // The agent survey finished (naturally or via early exit). Advance to the interest step.
  const handleOnboardingAgentComplete = useCallback((answeredCount: number) => {
    setAgentAnsweredCount(answeredCount);
    setOnboardingPhase('INTEREST');
  }, []);

  // The user picked their first chat — open it directly. Folded into a single setPanelState to avoid
  // clobbering state (see the note on onCreateChat below).
  const handleOnboardingFirstChat = useCallback(
    (result: CreateChatResult) => {
      setHasSeenOnboarding(true);
      setOnboardingPhase('AGENT');
      setPanelState({
        ...panelState,
        activeChat: {id: result.chatSessionId, type: result.chatType},
        activeSettingsTab: null,
        showOnboardingScreen: false,
      });
      refetchRef.current();
      if (doesAgentTypePreferExpandedSidepanel(result.initialAgentType)) {
        SidepanelActions.expand();
      }
    },
    [setHasSeenOnboarding, panelState, setPanelState]
  );

  // Start or resume the onboarding session from the backend so the agent task is queued and we hold
  // the canonical session id (including after a server-side reset or when entering the survey).
  const [onboardingChatSessionId, setOnboardingChatSessionId] = useState<string | null>(null);
  const [onboardingSessionError, setOnboardingSessionError] = useState<string | null>(null);

  useEffect(() => {
    if (!showOnboarding) {
      setOnboardingChatSessionId(null);
      setOnboardingSessionError(null);
      return;
    }
    let cancelled = false;
    startOnboardingSession()
      .then(({session_id: sessionId}) => {
        if (!cancelled) {
          setOnboardingChatSessionId(sessionId);
          setOnboardingSessionError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setOnboardingSessionError(ONBOARDING_START_ERROR_MESSAGE);
        }
        handleOnboardingStartError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [showOnboarding, showOnboardingScreen]);

  const handleRestartOnboarding = useCallback(async () => {
    await restartOnboardingSession();
    setOnboardingSessionError(null);
    setOnboardingChatSessionId(null);
    setHasSeenOnboarding(false);
    setOnboardingPhase('AGENT');
    setAgentAnsweredCount(0);
    setPanelState({
      ...panelState,
      activeSettingsTab: null,
      chatSettingsModal: null,
      showOnboardingScreen: true,
      showHistory: false,
    });
    startOnboardingSession()
      .then(({session_id: sessionId}) => {
        setOnboardingChatSessionId(sessionId);
      })
      .catch((err: unknown) => {
        setOnboardingSessionError(ONBOARDING_START_ERROR_MESSAGE);
        handleOnboardingStartError(err);
      });
  }, [setHasSeenOnboarding, panelState, setPanelState]);

  const handleSettingsTabChange = useCallback(
    (activeSettingsTab: AISettingsTab): void => {
      setPanelState({...panelState, activeSettingsTab});
    },
    [panelState, setPanelState]
  );

  /**
   * Here is an ASCII diagram of the layout we intend to achieve:
   *
   *     sidepanel
   *   +-------------------------------------+
   *   | [btns]   Benchling AI        [btns] |  header
   *   +-------------------------------------+
   *   | +----------+----------------------+ |  body
   *   | | drawer   | main content         | |
   *   | | +------+ | +-------+----------+ | |
   *   | | | his- | | | share |  chat    | | |
   *   | | | tory | | | panel |          | | |
   *   | | +------+ | +-------+----------+ | |
   *   | +---------+-----------------------+ |
   *   +-------------------------------------+
   *
   * When semi-expanded, the history drawer covers up the main content.
   * When expanded, the history drawer pushes the main content to the right.
   */

  const headerJsx = isSignedOut ? (
    <SignedOutHeader isShareLink={isShareLink} />
  ) : (
    <SignedInHeader
      isExpanded={props.isExpanded}
      isWideView={isWideView}
      isPhoneWidth={isPhoneWidth}
      isShareLink={isShareLink}
      hasChatAccess={hasChatAccess}
      isOnboarding={showOnboarding && showOnboardingScreen}
      isRedesignLayout={useRedesignLayout}
      onStarUpdated={handleStarUpdated}
    />
  );

  const panelBody = (
    <div className={css(styles.sidepanelBody)}>
      <div
        className={css(
          styles.mainContent,
          // Redesign uses the flex row (expanded) or minimized chrome; the legacy content-shift only
          // applies when the redesign layout is off.
          !useRedesignLayout && panelState.showHistory && isWideView && styles.mainContentShiftedForHistory
        )}
        {...getTestProps({
          component: 'AISidepanelV2',
          element: 'main-content',
        })}
      >
        {isSignedOut && window.bootstrap?.sharedFolderItemAPIId && (
          <div
            className={css(styles.sharedSidepanelContainer)}
            {...getTestProps({
              component: 'AISidepanelV2',
              element: 'sidepanel-container',
            })}
          >
            <SharedFileSidepanel useSidepanelStyling={false} />
          </div>
        )}
        <div className={classNames(css(styles.sidepanelChatContainer), 'scrollbar-override')}>
          {(() => {
            if (showOnboarding && showOnboardingScreen) {
              if (onboardingPhase === 'INTEREST') {
                return (
                  <OnboardingScreenEnterFade>
                    <AIInterestFirstChatScreen
                      onSubmitFirstChat={handleOnboardingFirstChat}
                      onSkip={dismissOnboardingToSettings}
                      onExit={dismissOnboardingToNewChat}
                      progressCount={agentAnsweredCount}
                      isExpanded={props.isExpanded}
                    />
                  </OnboardingScreenEnterFade>
                );
              }
              if (!onboardingChatSessionId) {
                return (
                  <OnboardingScreenEnterFade>
                    <OnboardingScreenShell
                      testComponent="AIOnboardingScreen"
                      onExit={dismissOnboardingToSettings}
                    >
                      <OnboardingQuestionLoadingView
                        message={onboardingSessionError ?? 'Starting onboarding and gathering information…'}
                        isExpanded={props.isExpanded}
                      />
                    </OnboardingScreenShell>
                  </OnboardingScreenEnterFade>
                );
              }
              return (
                <OnboardingScreenEnterFade>
                  <AIOnboardingScreen
                    chatSessionId={onboardingChatSessionId}
                    onChatSessionIdChange={setOnboardingChatSessionId}
                    onRetry={handleRestartOnboarding}
                    onComplete={handleOnboardingAgentComplete}
                    isExpanded={props.isExpanded}
                  />
                </OnboardingScreenEnterFade>
              );
            }
            if (panelState.showAgentFactoriesView) {
              return <AgentFactoriesListPage />;
            }
            if (panelState.activeChat.id == null) {
              if (hasChatAccess) {
                return (
                  <WelcomeToChatPanel
                    isNarrow={isPhoneWidth || !props.isExpanded}
                    onCreateChat={onCreateChat}
                    isSignedOut={isSignedOut}
                    voiceHandoffScopeId={voiceHandoffScopeId}
                    isOnboardingInviteExiting={isOnboardingInviteExiting}
                    onboardingBannerProps={
                      showOnboarding ? {onStart: handleBannerStart, onSkip: handleBannerSkip} : undefined
                    }
                  />
                );
              } else {
                return (
                  <NoAccessChatListing
                    dataTestAttrs={{
                      component: 'NoAccessChatListing',
                      element: 'root',
                      key: 'chatPanel',
                    }}
                  />
                );
              }
            } else if (isShareLink) {
              return <ReadOnlyChat chatId={panelState.activeChat.id} isExpanded={props.isExpanded} />;
            } else {
              return (
                <ChatRouter
                  key={panelState.clientChatId}
                  chat={panelState.activeChat}
                  isExpanded={props.isExpanded}
                />
              );
            }
          })()}
        </div>
      </div>
      {/* Legacy history drawer (also used for the no-access state). The redesign renders its own
          chrome (expanded sidebar / minimized dropdown), so this in-body drawer is skipped there. */}
      {!useRedesignLayout && (
        <div
          className={css(
            styles.drawer,
            !panelState.showHistory && isWideView && styles.closedDrawerOnFullyExpandedSidepanel,
            panelState.showHistory && isWideView && styles.openDrawerOnFullyExpandedSidepanel,
            !panelState.showHistory && !isWideView && styles.closedDrawerOnSemiExpandedSidepanel,
            panelState.showHistory && !isWideView && styles.openDrawerOnSemiExpandedSidepanel
          )}
          {...getTestProps({component: 'AISidepanel', element: 'drawer'})}
        >
          <div className={classNames(css(styles.chatListingContainer), 'scrollbar-override')}>
            {hasChatAccess ? (
              <ChatListing
                selectedChatSession={panelState.activeChat}
                onSelectChatSession={onOpenChat}
                refetchRef={refetchRef}
              />
            ) : (
              <NoAccessChatListing
                dataTestAttrs={{component: 'NoAccessChatListing', element: 'root', key: 'chatListing'}}
              />
            )}
          </div>
        </div>
      )}
      {panelState.activeSettingsTab != null && (
        <div
          className={css(styles.settingsDrawer)}
          {...getTestProps({component: 'AISidepanelV2', element: 'settings-overlay'})}
        >
          <AISettingsContent
            chatId={panelState.activeChat.id}
            isExpanded={props.isExpanded}
            activeTab={panelState.activeSettingsTab}
            onTabChange={handleSettingsTabChange}
            onRestartOnboarding={handleRestartOnboarding}
          />
        </div>
      )}
      {llmEnableChatRedesign && <ChatSettingsModalContainer onRestartOnboarding={handleRestartOnboarding} />}
    </div>
  );

  const sidepanelContent = (
    <div
      className={classNames(
        'sidepanel',
        css(styles.sidepanel, (isSignedOut || isPhoneWidth) && styles.sidepanelCoveringNavBar)
      )}
    >
      {/* Wrapper: redesign = relative box holding one absolute drawer + the main column; legacy =
          display:contents (header/body remain direct children of .sidepanel, unchanged). */}
      <div
        ref={redesignRootRef}
        className={css(useRedesignLayout ? styles.redesignRoot : styles.layoutPassthrough)}
        {...getTestProps({component: 'AISidepanel', element: 'redesign-root'})}
        // Drive the drawer width (and the main column's matching inset) from the resize state. Only
        // set while the drawer is resizable so the default CSS width applies otherwise.
        style={
          isDrawerResizable
            ? ({[REDESIGN_DRAWER_WIDTH_CSS_VAR]: `${drawerWidth}px`} as React.CSSProperties)
            : undefined
        }
      >
        {useRedesignLayout ? (
          <>
            {/* Click-away target while the semi-expanded dropdown is open. */}
            {!isWideView && isDrawerOpen && (
              <div
                role="button"
                aria-label="Close navigation"
                tabIndex={-1}
                className={css(styles.redesignBackdrop)}
                onClick={onToggleSidebar}
              />
            )}
            {/* One persistent drawer: a left column in the wide view, a top dropdown in the
                semi-expanded view. It reveals by unclipping (growing width / max-height with
                overflow:hidden), and the transition is gated (see isDrawerAnimated) so switching
                views re-clips instantly instead of animating between the two axes. */}
            <div
              className={css(
                styles.redesignDrawer,
                isWideView ? styles.redesignDrawerExpanded : styles.redesignDrawerSemi,
                isDrawerOpen
                  ? isWideView
                    ? styles.redesignDrawerExpandedOpen
                    : styles.redesignDrawerSemiOpen
                  : isWideView
                    ? styles.redesignDrawerExpandedClosed
                    : styles.redesignDrawerSemiClosed,
                // Suppress the width transition while dragging so the drawer tracks the cursor.
                isDrawerAnimated && !isDrawerResizing && styles.redesignDrawerAnimated
              )}
              {...getTestProps({component: 'AISidepanel', element: 'drawer'})}
            >
              {isWideView ? (
                <RedesignChatSidebar
                  selectedChatSession={panelState.activeChat}
                  onSelectChatSession={onOpenChat}
                  onNewChat={onNewChat}
                  isDrawerOpen={isDrawerOpen}
                  refetchRef={refetchRef}
                  historyActionsRef={historyActionsRef}
                />
              ) : (
                <div className={classNames(css(styles.redesignDropdownInner), 'scrollbar-override')}>
                  <ChatSidebarNav onNewChat={onNewChat} />
                  <GroupedChatListing
                    selectedChatSession={panelState.activeChat}
                    onSelectChatSession={onOpenChat}
                    refetchRef={refetchRef}
                    historyActionsRef={historyActionsRef}
                    fillHeight={false}
                  />
                </div>
              )}
            </div>
            <div
              className={css(
                styles.redesignMainColumn,
                isWideView && isDrawerOpen && styles.redesignMainColumnShifted,
                isDrawerAnimated && !isDrawerResizing && styles.redesignMainColumnAnimated
              )}
            >
              {isWideView ? (
                <>
                  <RedesignSidebarToggle onToggle={onToggleSidebarFromHamburger} />
                  {headerJsx}
                </>
              ) : (
                <RedesignMinimizedHeader
                  activeChatId={panelState.showAgentFactoriesView ? null : panelState.activeChat.id}
                  isMenuOpen={panelState.showHistory}
                  onToggleMenu={onToggleSidebarFromHamburger}
                  onExpandPanel={onExpandPanelFromSidebar}
                  onNewChat={onNewChat}
                  isPhoneWidth={isPhoneWidth}
                  onStarUpdated={handleStarUpdated}
                />
              )}
              {panelBody}
            </div>
            {/* Drag handle on the drawer's right edge (wide view only), so users can widen the
                sidebar to reveal more of the chat list. */}
            {isDrawerResizable && (
              <DrawerResizeHandle isResizing={isDrawerResizing} onMouseDown={onDrawerResizeMouseDown} />
            )}
          </>
        ) : (
          <>
            {headerJsx}
            {panelBody}
          </>
        )}
      </div>
    </div>
  );

  return isSignedOut ? (
    sidepanelContent
  ) : (
    <MCPEnabledServersProvider>{sidepanelContent}</MCPEnabledServersProvider>
  );
}

function transitionValuesForDrawerEffect(properties: Array<string>): string {
  return properties.map((property) => `${property} 0.3s ease-in-out`).join(', ');
}

const PADDING = 16;

/** @visibleForTesting */
export const INTRO_LAYOUT_SWAP_DELAY_MS = 260;
/** @visibleForTesting */
export const INTRO_CONTENT_ENTER_MS = 420;
const INTRO_CONTENT_OFFSET_PX = 14;

const ONBOARDING_START_ERROR_MESSAGE = 'Could not start onboarding. Refresh the page and try again.';

interface OnboardingWelcomeStyleProps {
  isExpanded: boolean;
  isOnboarding: boolean;
}

const useOnboardingWelcomeStyles = createStyles(
  (theme, {isExpanded, isOnboarding}: OnboardingWelcomeStyleProps) => ({
    container: {
      flex: 1,
      minHeight: '100%',
      maxWidth: 714,
      width: '100%',
      padding: `0 ${PADDING}px`,
      margin: '0 auto',
      display: 'grid',
      gridTemplateAreas: !isExpanded
        ? isOnboarding
          ? '"academic" "spacerTop" "banner" "spacerMiddle" "disclosure" "input" "warning" "spacerBottom"'
          : '"academic" "spacerTop" "banner" "disclosure" "input" "warning" "spacerBottom"'
        : '"academic" "spacerTop" "banner" "input" "warning" "disclosure" "spacerBottom"',
      gridTemplateRows: !isExpanded
        ? isOnboarding
          ? 'auto minmax(0, 1fr) auto minmax(0, 1fr) auto auto auto 0'
          : 'auto minmax(0, 1fr) auto auto auto auto 0'
        : isOnboarding
          ? 'auto minmax(0, 0.9fr) auto auto 0 auto minmax(0, 1.4fr)'
          : 'auto minmax(0, 4fr) auto auto 0 auto minmax(0, 6fr)',
    },
    academicBanner: {
      gridArea: 'academic',
    },
    banner: {
      gridArea: 'banner',
      minWidth: 0,
      willChange: isOnboarding ? 'opacity, transform' : undefined,
    },
    chatInput: {
      gridArea: 'input',
      minWidth: 0,
      // In the minimized layout the category pills sit directly above the compose box, so this is the
      // pill/compose seam; in the expanded layout it separates the compose box from the hero.
      marginTop: theme.spacing.xl,
      willChange: 'opacity, transform',
    },
    warning: {
      gridArea: 'warning',
    },
    disclosure: {
      gridArea: 'disclosure',
      minWidth: 0,
      marginTop: isOnboarding && isExpanded ? 12 : 0,
    },
    spacerTop: {
      gridArea: 'spacerTop',
      minHeight: 0,
    },
    spacerMiddle: {
      gridArea: 'spacerMiddle',
      minHeight: 0,
    },
    spacerBottom: {
      gridArea: 'spacerBottom',
      minHeight: 0,
    },
  })
);

interface OnboardingWelcomeLayoutProps {
  isExpanded: boolean;
  isOnboarding: boolean;
  newChatInput: React.ReactNode;
  renderWelcomeBanner: (layoutIsExpanded: boolean, isLayoutContentVisible: boolean) => React.ReactNode;
  showProgressiveDisclosure: boolean;
  suggestedActions: React.ReactNode;
  hasUserInput: boolean;
  isOnboardingInviteExiting: boolean;
  onHoverOption: (prompt: string | null) => void;
  onSelectOption: (prompt: string) => void;
}

function OnboardingWelcomeLayout({
  isExpanded,
  isOnboarding,
  newChatInput,
  renderWelcomeBanner,
  showProgressiveDisclosure,
  suggestedActions,
  hasUserInput,
  isOnboardingInviteExiting,
  onHoverOption,
  onSelectOption,
}: OnboardingWelcomeLayoutProps): JSX.Element {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [layoutIsExpanded, setLayoutIsExpanded] = useState(isExpanded);
  const [isComposeEntered, setIsComposeEntered] = useState(true);
  const [isDisclosureVisible, setIsDisclosureVisible] = useState(true);
  const previousIsExpandedRef = useRef(isExpanded);
  const disclosureEnterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composeEnterFrameRef = useRef<number | null>(null);
  const composeEnterSecondFrameRef = useRef<number | null>(null);
  const {classes} = useOnboardingWelcomeStyles({
    isExpanded: layoutIsExpanded,
    isOnboarding,
  });

  const clearPendingEntrance = useCallback(() => {
    if (disclosureEnterTimerRef.current != null) {
      clearTimeout(disclosureEnterTimerRef.current);
      disclosureEnterTimerRef.current = null;
    }
    if (composeEnterFrameRef.current != null) {
      cancelAnimationFrame(composeEnterFrameRef.current);
      composeEnterFrameRef.current = null;
    }
    if (composeEnterSecondFrameRef.current != null) {
      cancelAnimationFrame(composeEnterSecondFrameRef.current);
      composeEnterSecondFrameRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    if (previousIsExpandedRef.current === isExpanded) {
      return;
    }
    previousIsExpandedRef.current = isExpanded;
    clearPendingEntrance();

    if (prefersReducedMotion || !showProgressiveDisclosure || hasUserInput) {
      setLayoutIsExpanded(isExpanded);
      setIsComposeEntered(true);
      setIsDisclosureVisible(true);
      return;
    }

    // The shell swaps layouts immediately; only its incoming contents animate. Disabling the
    // transition while hidden makes the reset instantaneous instead of playing an exit animation.
    setLayoutIsExpanded(isExpanded);
    setIsComposeEntered(false);
    setIsDisclosureVisible(false);

    if (isExpanded) {
      // Let the hard layout swap paint in its hidden state, then enter the expanded compose box.
      composeEnterFrameRef.current = requestAnimationFrame(() => {
        composeEnterFrameRef.current = null;
        composeEnterSecondFrameRef.current = requestAnimationFrame(() => {
          composeEnterSecondFrameRef.current = null;
          setIsComposeEntered(true);
        });
      });
    }

    disclosureEnterTimerRef.current = setTimeout(() => {
      disclosureEnterTimerRef.current = null;
      setIsComposeEntered(true);
      setIsDisclosureVisible(true);
    }, INTRO_LAYOUT_SWAP_DELAY_MS);
  }, [clearPendingEntrance, hasUserInput, isExpanded, prefersReducedMotion, showProgressiveDisclosure]);

  useEffect(() => clearPendingEntrance, [clearPendingEntrance]);

  const composeEntranceStyle: React.CSSProperties = {
    opacity: isComposeEntered ? 1 : 0,
    transform: isComposeEntered ? 'translateY(0)' : `translateY(${INTRO_CONTENT_OFFSET_PX}px)`,
    transition:
      prefersReducedMotion || !isComposeEntered
        ? 'none'
        : `opacity ${INTRO_CONTENT_ENTER_MS}ms ease, transform ${INTRO_CONTENT_ENTER_MS}ms ease`,
  };

  return (
    <OnboardingInviteExitFade exiting={isOnboarding && isOnboardingInviteExiting}>
      <div
        className={classes.container}
        {...getTestProps({
          component: 'WelcomeToChatPanel',
          element: 'root',
        })}
      >
        <div className={classes.academicBanner}>
          <AcademicFreeChatBannerIfVisible />
        </div>
        <div className={classes.spacerTop} aria-hidden={true} />
        <div
          className={classes.banner}
          style={isOnboarding ? composeEntranceStyle : undefined}
          {...getTestProps({component: 'WelcomeToChatPanel', element: 'banner'})}
        >
          {renderWelcomeBanner(layoutIsExpanded, isComposeEntered)}
        </div>
        {isOnboarding && !layoutIsExpanded && <div className={classes.spacerMiddle} aria-hidden={true} />}
        <div
          className={classes.chatInput}
          style={composeEntranceStyle}
          {...getTestProps({component: 'WelcomeToChatPanel', element: 'chatInput'})}
        >
          {newChatInput}
        </div>
        <div className={classes.warning}>{!layoutIsExpanded && <AIWarning />}</div>
        <div
          className={classes.disclosure}
          {...getTestProps({component: 'WelcomeToChatPanel', element: 'progressiveDisclosure'})}
        >
          {showProgressiveDisclosure && (
            <ProgressiveDisclosure
              hideImmediately={!isComposeEntered}
              isStacked={!layoutIsExpanded}
              isVisible={!hasUserInput && isDisclosureVisible}
              onHoverOption={onHoverOption}
              onSelectOption={onSelectOption}
              preserveHiddenGeometry={layoutIsExpanded || !hasUserInput}
            />
          )}
          {suggestedActions}
        </div>
        <div className={classes.spacerBottom} aria-hidden={true} />
      </div>
    </OnboardingInviteExitFade>
  );
}

function handleOnboardingStartError(err: unknown): void {
  if (!isServerRequestError(err)) {
    throw err;
  }
}

const styles = StyleSheet.create({
  headerButtonsSignedOut: {
    display: 'flex',
    gap: '4px',
  },
  welcomeHeader: {
    lineHeight: '26px',
    fontWeight: 600,
    marginTop: '20px',
  },
  welcomeHero: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  welcomeHeroCompact: {
    marginBottom: 0,
  },
  welcomeFooter: {
    marginTop: '44px',
    paddingBottom: '20px',
  },
  chatListingContainer: {
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  sidepanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  // Legacy: transparent pass-through so header/body remain direct flex children of `.sidepanel`.
  layoutPassthrough: {
    display: 'contents',
  },
  // Redesign: a relative box that holds one absolutely-positioned drawer (overlaying the left when
  // expanded, the top when semi-expanded) plus the in-flow main column. Also the positioning context
  // for the floating hamburger and the drawer. `overflow:hidden` clips the drawer's hidden position.
  // The 1px transparent right border keeps our opaque surfaces (the lavender drawer/header) from
  // painting over `.sidepanel`'s inset right divider + drop shadow, which the legacy transparent
  // content leaves visible; `box-sizing: border-box` keeps the root exactly the panel width.
  redesignRoot: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
    borderRight: '1px solid transparent',
  },
  redesignMainColumn: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // When the expanded sidebar is open, shift the main column right so it sits beside (not under) the
  // drawer. Semi-expanded doesn't shift — the top dropdown overlays the body.
  redesignMainColumnShifted: {
    // Matches the drawer's (resizable) width so the chat/compose column sits beside it and stays
    // centered in the remaining canvas as the drawer grows.
    paddingLeft: `var(${REDESIGN_DRAWER_WIDTH_CSS_VAR}, ${REDESIGN_DRAWER_WIDTH_PX}px)`,
  },
  redesignMainColumnAnimated: {
    transition: transitionValuesForDrawerEffect(['padding-left']),
  },
  publicTrialBanner: {
    textAlign: 'left',
    marginTop: 20,
  },
  // In some scenarios (mobile and logged out view), we don't want the navbar to be visible at all,
  // so implement that by just covering it up.
  sidepanelCoveringNavBar: {
    left: 0,
    zIndex: ZINDEX_SIDE_NAVBAR + 1,
    transition: 'none',
  },
  sidepanelHeaderSignedOut: {
    padding: '16px 8px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  sidepanelHeaderSignedOutShareLink: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  sidepanelHeader: {
    padding: '16px 8px 14px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: 3,
  },
  leftHeaderButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '0px',
    overflow: 'visible',
    transition: transitionValuesForDrawerEffect(['width']),
    zIndex: 1,
  },
  rightHeaderButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '0px',
    overflow: 'visible',
    zIndex: 1,
    justifyContent: 'flex-end',
  },
  leftHeaderButtonsExpanded: {
    width: HISTORY_DRAWER_WIDTH - 8,
  },
  headerTitle: {
    fontSize: '18px',
    lineHeight: '24px',
    flex: 1,
    textAlign: 'center',
    zIndex: 0,
  },
  headerTitleSignedOut: {
    marginLeft: 16,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 22,
  },
  headerTitleLogoSignedOut: {
    color: '#2B39F1',
    marginRight: 5,
  },
  headerTitleTextSignedOut: {
    background: 'linear-gradient(to right, #000DB5, #9A4BD7)',
    color: 'transparent',
    backgroundClip: 'text',
  },
  sidepanelBody: {
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  sidepanelChatContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  drawer: {
    backgroundColor: 'white',
    position: 'absolute',
    flex: 'none',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    flexDirection: 'column',
    transition: transitionValuesForDrawerEffect(['width', 'left']),
    zIndex: 2,
    top: 0,
    bottom: 0,
  },
  closedDrawerOnSemiExpandedSidepanel: {
    width: '100%',
    left: '-100%',
  },
  // Redesign sidebar drawer: one absolutely-positioned element used for both views. It reveals by
  // *unclipping* (growing width when wide, max-height when semi-expanded) with overflow:hidden, so the
  // content stays put and is clipped rather than sliding, and a closed drawer shows nothing. The
  // transition is only applied while animating and never on the view-flip render (see isDrawerAnimated),
  // so switching views repositions the drawer instantly instead of animating diagonally.
  redesignDrawer: {
    position: 'absolute',
    boxSizing: 'border-box',
    backgroundColor: REDESIGN_DRAWER_BACKGROUND,
    boxShadow: REDESIGN_DRAWER_INNER_SHADOW,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 5,
  },
  // Wide: a full-height left column revealed by growing its width; content stays a fixed width and is
  // clipped by overflow:hidden (no reflow).
  redesignDrawerExpanded: {
    top: 0,
    bottom: 0,
    left: 0,
  },
  redesignDrawerExpandedOpen: {
    // Width is driven by a CSS variable (set inline from the resize state) so the user can drag it
    // wider; falls back to the default width when unset. Referencing the variable keeps Aphrodite's
    // `!important` output honoring the dynamic value.
    width: `var(${REDESIGN_DRAWER_WIDTH_CSS_VAR}, ${REDESIGN_DRAWER_WIDTH_PX}px)`,
    padding: `${REDESIGN_DRAWER_VERTICAL_PADDING_PX}px ${REDESIGN_DRAWER_HORIZONTAL_PADDING_PX}px`,
    borderRight: `1px solid ${REDESIGN_DRAWER_BORDER}`,
  },
  redesignDrawerExpandedClosed: {
    width: 0,
    padding: `${REDESIGN_DRAWER_VERTICAL_PADDING_PX}px 0`,
  },
  // Semi-expanded: a full-width dropdown beneath the 54px header revealed by growing its max-height.
  redesignDrawerSemi: {
    top: MINIMIZED_HEADER_HEIGHT,
    left: 0,
    right: 0,
  },
  redesignDrawerSemiOpen: {
    maxHeight: `calc(100dvh - ${MINIMIZED_HEADER_HEIGHT}px)`,
    borderBottom: `1px solid ${REDESIGN_DRAWER_BORDER}`,
  },
  redesignDrawerSemiClosed: {
    maxHeight: 0,
  },
  redesignDrawerAnimated: {
    transition: transitionValuesForDrawerEffect(['width', 'max-height', 'padding']),
  },
  // Scroll container for the semi-expanded dropdown's nav + chat list.
  redesignDropdownInner: {
    maxHeight: `calc(100dvh - ${MINIMIZED_HEADER_HEIGHT}px)`,
    overflowY: 'auto',
    padding: '18px 16px',
  },
  // Sits below the drawer (zIndex 5) but above the chat body; closes the dropdown on outside click.
  redesignBackdrop: {
    position: 'absolute',
    top: MINIMIZED_HEADER_HEIGHT,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 4,
  },
  redesignHeader: {
    borderBottom: 'none',
    // Match the minimized header's bar height so the right-side controls center on the same band as
    // the floating hamburger (which is centered in that same height) instead of on the taller
    // two-line title. The horizontal inset mirrors the hamburger's, keeping both edges symmetric.
    height: MINIMIZED_HEADER_HEIGHT,
    padding: `0 ${REDESIGN_HEADER_ICON_CHIP_INSET_PX}px`,
  },
  // The share button is `ui-library`'s small icon button, whose geometry the header's icon chip
  // already mirrors, so it only needs the chip's hover background. That has to be drawn by this
  // wrapper because ButtonStyle.TRANSPARENT pins the button's own background to transparent.
  redesignShareChip: {
    display: 'inline-flex',
    borderRadius: REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
    color: REDESIGN_HEADER_ICON_COLOR,
    ':hover': {
      backgroundColor: REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
    },
  },
  closedDrawerOnFullyExpandedSidepanel: {
    width: HISTORY_DRAWER_WIDTH,
    left: -HISTORY_DRAWER_WIDTH,
  },
  openDrawerOnSemiExpandedSidepanel: {
    width: '100%',
    left: 0,
  },
  openDrawerOnFullyExpandedSidepanel: {
    width: HISTORY_DRAWER_WIDTH,
    left: 0,
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    position: 'absolute',
    display: 'flex',
    transition: transitionValuesForDrawerEffect(['left']),
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    '@media (min-width: 768px)': {
      // Avoids drawer animation from squishing content when switching sidepanels.
      minWidth: SIDEPANEL_SEMI_EXPANDED_WIDTH,
    },
  },
  mainContentShiftedForHistory: {
    left: HISTORY_DRAWER_WIDTH,
  },
  sharedSidepanelContainer: {
    display: 'flex',
    width: 360,
  },
  settingsDrawer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
  },
});

export default AISidepanel;
